import * as $ from 'jquery';

const COL_CLASS_PATTERN = /col\-(\d+)/;
const ROW_CLASS_PATTERN = /row\-(\d+)/;
const CELL_CONTENT_HIDDEN_CLASS = 'is-hidden';
const CELL_CONTENT_VIEW_TEMPLATE = `<div class="cell-content-view ${CELL_CONTENT_HIDDEN_CLASS}" />`;

export default class CellSelect {
  static NAMESPACE = 'axel-cell-select';

  private opts;
  public label;
  private renderer;
  private state;
  private $tbody;
  private $contentView;
  private $container;

  constructor(renderer, opts) {
    this.renderer = renderer;
    this.opts = opts;
    this.label = 'cell-select';
    this.$contentView = this.createContentView();
    this.state = {
      selectedCell: {
        row: null,
        col: null,
      },
    };
  }

  /**
   * [HookMethod] Build Body
   * @param tbodyEl
   * @param containerEl
   * @returns {any}
   */
  public buildBody(tbodyEl, containerEl) {
    const $tbody = (this.$tbody = $(tbodyEl));
    this.$container = $(containerEl);

    $tbody.on(this.namespace('dblclick'), this.requestCellContentViewerHandler.bind(this));
    $tbody.on(this.namespace('click'), this.destroyCellContentViewerHandler.bind(this));
    return tbodyEl;
  }

  /**
   * [HookMethod] Build Table
   * @param tableEl
   * @param containerEl
   * @returns {any}
   */
  public buildTable(tableEl, containerEl) {
    $(tableEl).addClass(CellSelect.NAMESPACE);
    return tableEl;
  }

  /**
   * [HookMethod] After Render
   * @param tableEl
   * @param containerEl
   * @returns {any}
   */
  public afterRender(tableEl, containerEl) {
    this.$contentView.appendTo(tableEl);
    return tableEl;
  }

  /**
   * [HookMethod] Destroy
   * @param _
   */
  public destroy(_) {
    this.$contentView.remove();
    this.$contentView = null;
    this.$tbody.off(this.namespace('dblclick'));
    this.$tbody.off(this.namespace('click'));
  }

  private namespace(evtName) {
    return evtName + '.' + CellSelect.NAMESPACE;
  }

  /**
   * Create Content View
   *
   * Instantiate and bind events to cell content view
   * @returns {JQuery}
   */
  private createContentView() {
    const handleScroll = function (e) {
      const $this = $(this);
      const deltaY = e.originalEvent.deltaY;
      const bottomBoundary = $this.get(0).scrollHeight - $this.innerHeight();

      // prevent parent from scrolling
      if (deltaY < 0 && $this.scrollTop() === 0) {
        e.preventDefault();
      } else if (deltaY > 0 && $this.scrollTop() === bottomBoundary) {
        e.preventDefault();
      }
    };

    return (
      $(CELL_CONTENT_VIEW_TEMPLATE)
        .on('wheel', handleScroll)
        // prevent click from destroying content view
        .on('click', (evt) => evt.stopPropagation())
    );
  }

  /**
   * Request Cell Content Viewer Handler
   *
   * Handle qualifying event and display cell content viewer
   * @param evt
   */
  private requestCellContentViewerHandler(evt) {
    const targetEl = evt.target;

    if (this.isCell(targetEl)) {
      this.selectCellFromEl(targetEl);
      this.revealCellContent();
    }
  }

  /**
   * Destroy Cell Content Viewer Handler
   *
   * Hide content view
   * @param evt
   */
  private destroyCellContentViewerHandler(evt) {
    this.$contentView.addClass(CELL_CONTENT_HIDDEN_CLASS);
  }

  /**
   * Is Cell
   *
   * Determine if element is a table cell
   * @param el
   * @returns {boolean}
   */
  private isCell(el) {
    return COL_CLASS_PATTERN.test(el.className) && ROW_CLASS_PATTERN.test(el.className);
  }

  /**
   * Select Cell From Element
   *
   * Try to update selected cell state from cell table coordinates
   * @param el
   */
  private selectCellFromEl(el) {
    const { row, col } = this.getCellCoordsFromEl(el);

    this.updateSelectedCell(row, col);
  }

  /**
   * Get Cell Coords From Element
   *
   * Retrieve the relative row index and column index of the given cell element
   * @param el
   * @returns {{row: number, col: number}}
   */
  private getCellCoordsFromEl(el) {
    const className = el.className;
    const getColIndex = (str) => parseInt(className.match(COL_CLASS_PATTERN)[1]);
    const getRowIndex = (str) => parseInt(className.match(ROW_CLASS_PATTERN)[1]);
    const col = getColIndex(className);
    const row = getRowIndex(className);

    return { row, col };
  }

  private updateSelectedCell(row, col) {
    if (row && col) {
      this.state.selectedCell.row = row;
      this.state.selectedCell.col = col;
    } else {
      throw new Error('Invalid table cell coordinates');
    }
  }

  /**
   * Reveal Cell Content
   *
   * Populate and position cell content view from selected cell dimensions
   */
  private revealCellContent() {
    const { row, col } = this.getSelectedCellCoords();

    if (this.cellIsSelected) {
      const content = this.getSelectedCellContentByCoords(row, col);
      const { x, y } = this.getSelectedCellElementPosition();
      const { w, h } = this.getContentViewBounds(x, y);

      this.renderCellContentView(content, x, y, w, h);
    }
  }

  /**
   * Get Selected Cell Coords
   *
   * Retrieve table row and column coordinates from stored state
   * @returns {{row: null, col: null}}
   */
  private getSelectedCellCoords() {
    const row = this.state.selectedCell.row;
    const col = this.state.selectedCell.col;

    return { row, col };
  }

  /**
   * Get Selected Cell Element Position
   *
   * Retrieve cell position relative to the scrollable container
   * @returns {{x: any, y: any}}
   */
  private getSelectedCellElementPosition() {
    const tdEl = this.getSelectedCellElement();
    const childPos = $(tdEl).offset();
    const parentPos = this.$container.offset();
    const y = childPos.top - parentPos.top + this.$container.scrollTop();
    const x = childPos.left - parentPos.left + this.$container.scrollLeft();

    return { x, y };
  }

  /**
   * Get Content View Bounds
   *
   * Retrieve valid dimensions of content view given the container's bounds
   * @param x
   * @param y
   * @returns {{w: number, h: number}}
   */
  private getContentViewBounds(x, y) {
    const maxW = this.getSelectedCellElement().offsetWidth;
    const { height, width } = this.$container.get(0).getBoundingClientRect();
    const h = height + this.$container.scrollTop() - y;
    let w = width + this.$container.scrollLeft() - x;

    if (maxW < w) {
      w = maxW;
    }
    return { w, h };
  }

  private cellIsSelected() {
    const { row, col } = this.getSelectedCellCoords();

    return !!(row && col);
  }

  /**
   * Get Selected Cell Content By Coordinates
   *
   * Retrieve the text content of the cell element located under the given
   * row index and column index
   * @param row
   * @param col
   * @returns {string}
   */
  private getSelectedCellContentByCoords(row, col) {
    const getHTMLContentByCoords = (row, col) => $(this.getSelectedCellElement()).text();
    return 'function' === typeof this.opts.getCellContentByCoords
      ? this.opts.getCellContentByCoords(row, col)
      : getHTMLContentByCoords(row, col);
  }

  /**
   * Get Selected Cell Element
   *
   * Retrieve DOM element of the cell that is currently selected
   * @returns {any}
   */
  private getSelectedCellElement() {
    const { row, col } = this.getSelectedCellCoords();
    const rowClassLayout = (n) => 'row-' + n;
    const colClassLayout = (n) => 'col-' + n;

    return this.$tbody.find(`.${rowClassLayout(row)}.${colClassLayout(col)}`).get(0);
  }

  /**
   * Render Cell Content View
   *
   * Inject text content and apply dimensions to content view.  Absolutely position
   * content view element to given x,y coordinates
   * @param str
   * @param x
   * @param y
   * @param w
   * @param h
   */
  private renderCellContentView(str, x, y, w, h) {
    this.$contentView.css({
      position: 'absolute',
      left: x,
      top: y,
      width: w,
      maxHeight: h,
    });
    this.$contentView.text(str);
    this.$contentView.removeClass(CELL_CONTENT_HIDDEN_CLASS);
  }
}
